import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, hotTags } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = category
    ? articles.filter((a) => a.categorySlug === category.slug)
    : [];

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-bold text-foreground-900">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 黑料不打烊`,
    description: category.description,
    url: `https://heiliaobudaying.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '黑料不打烊',
      url: 'https://heiliaobudaying.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-4 mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-lg">
                    <i className={`${category.icon} text-white text-lg`}></i>
                  </div>
                  <div>
                    <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                      {category.name}
                    </h1>
                    <p className="text-sm text-foreground-600 mt-0.5">
                      {category.description} · 共 {categoryArticles.length} 篇
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1 min-w-0">
                  {categoryArticles.length === 0 ? (
                    <div className="text-center py-16 text-foreground-500">
                      <i className="ri-inbox-line text-4xl block mb-3"></i>
                      <p>该分类下暂无文章</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {categoryArticles.map((article) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.slug}`}
                          className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 no-underline"
                        >
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={article.image}
                              alt={article.title}
                              title={`黑料不打烊 ${article.category} ${article.title}`}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute top-3 left-3">
                              <span className="px-2.5 py-1 rounded text-xs font-medium bg-primary-500 text-white whitespace-nowrap">
                                {article.category}
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <h2 className="text-base font-semibold font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug mb-3">
                              {article.title}
                            </h2>
                            <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                              {article.excerpt}
                            </p>
                            <div className="flex items-center justify-between text-xs text-foreground-500">
                              <span className="flex items-center gap-1">
                                <i className="ri-time-line"></i>
                                {article.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-eye-line"></i>
                                {(article.views / 10000).toFixed(1)}万阅读
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-full lg:w-72 flex-shrink-0">
                  <div className="space-y-5 sticky top-20">
                    <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                      <h3 className="text-sm font-bold font-heading text-foreground-900 mb-3 flex items-center gap-2">
                        <i className="ri-folder-line text-primary-500"></i>
                        全部分类
                      </h3>
                      <div className="space-y-1">
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/category/${cat.slug}`}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors no-underline ${
                              cat.slug === slug
                                ? 'bg-primary-500 text-white'
                                : 'text-foreground-600 hover:bg-background-100'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <i className={`${cat.icon} text-sm`}></i>
                              {cat.name}
                            </span>
                            <span className="text-xs opacity-80">{cat.count}</span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                      <h3 className="text-sm font-bold font-heading text-foreground-900 mb-3 flex items-center gap-2">
                        <i className="ri-hashtag text-accent-500"></i>
                        热门标签
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {hotTags.slice(0, 10).map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap cursor-default"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}